import {useState, useEffect, useMemo} from 'react'
import {Modal, FormError} from '@components/lib'
import * as PropTypes from 'prop-types'
import {useTranslation} from 'react-i18next'
import {useMutation} from '@tanstack/react-query'

import {useApi} from 'hooks'
import {cacheKeys} from 'api'
import {TokenInfo} from 'constData'
import useRefetchQuery from 'hooks/helpers/useRefetchQuery'

import {
  Description,
  ButtonBottom,
  ButtonContainer,
  SuccessMessage,
  ErrorMessage,
} from './ModalTokenTransfer.styles'

const ModalTokenTransfer = ({buttonText, onClose, convertData, ...modalProps}) => {
  const {refetchQuery} = useRefetchQuery()
  const {t} = useTranslation()
  const [status, setStatus] = useState(false)
  const api = useApi()

  const convertedAmount = useMemo(() => {
    const V999 = convertData.total_v999_amount * 2
    return {
      GNS: convertData.total_gns_amount,
      V999,
      totalAmount: convertData.total_gns_amount + V999,
    }
  }, [convertData])

  const {
    mutate: tokenTransfer,
    isLoading,
    isSuccess,
    isError,
    error: tokenTransferError,
  } = useMutation(() => api.tokenTransfer({convert_to: convertData.convert_to}), {
    onSuccess: async ({success}) => {
      if (success) {
        setStatus(true)
        refetchQuery(
          [cacheKeys.balanceTotal],
          [cacheKeys.legacyWallets],
          [cacheKeys.asset, TokenInfo.MBASE.apiId],
          [cacheKeys.tokenConvertEligibility, 'V999GNS'],
          [cacheKeys.legacyWallets, TokenInfo.GNS.apiId],
          [cacheKeys.legacyWallets, TokenInfo.V999.apiId],
          [cacheKeys.tokenConvertEligibility, TokenInfo.GNS.apiId],
          [cacheKeys.tokenConvertEligibility, TokenInfo.V999.apiId],
        )
      }
    },
    onError: () => setStatus(true),
  })

  useEffect(() => {
    if (modalProps?.isOpened) {
      setStatus(false)
    }
  }, [modalProps.isOpened])

  return (
    <Modal onClose={onClose} {...modalProps}>
      {status ? (
        <>
          {isSuccess && (
            <SuccessMessage>{t('wallet.assets.tokenTransferSuccessMessage')}</SuccessMessage>
          )}
          {isError && <FormError error={tokenTransferError.errorsObject} />}
        </>
      ) : !convertData.availableToken ? (
        <ErrorMessage>You have no amount</ErrorMessage>
      ) : (
        <>
          <Description>
            Your available balance is {convertData.total_v999_amount} {TokenInfo.V999.apiId} and{' '}
            {convertData.total_gns_amount} {TokenInfo.GNS.apiId}. Your{' '}
            {convertData.total_v999_amount} {TokenInfo.V999.apiId} will be converted right now to{' '}
            {convertedAmount.V999} {TokenInfo.MBASE.apiId}.
            <br />
            So your converted amount will be {convertedAmount.totalAmount} {TokenInfo.MBASE.apiId}.
            Do you want to proceed?
          </Description>
          <ButtonContainer>
            <div>
              <ButtonBottom
                minWidth='110px'
                size='sm'
                onClick={tokenTransfer}
                isLoading={isLoading}
              >
                {buttonText || t('buttons.ok')}
              </ButtonBottom>
            </div>
            <div>
              <ButtonBottom minWidth='110px' size='sm' onClick={onClose} nature='reject'>
                {buttonText || t('buttons.no')}
              </ButtonBottom>
            </div>
          </ButtonContainer>
        </>
      )}
    </Modal>
  )
}

ModalTokenTransfer.propTypes = {
  buttonText: PropTypes.string,
  onClose: PropTypes.func,
  convertData: PropTypes.object,
}
ModalTokenTransfer.defaultProps = {
  buttonText: null,
  onClose: null,
  convertData: {},
}

export default ModalTokenTransfer
